/**
 * Storage-based key-value database implementation using localStorage or custom Storage
 * @example
 * ```ts
 * const db = new StorageKv<{ theme: string; lang: string }>();
 * await db.set('theme', 'dark');
 * const theme = await db.get('theme');
 * ```
 */
export class StorageKv {
    storage;
    /** Creates a new StorageKv instance */
    constructor(storage) {
        this.storage =
            storage ?? (typeof window !== "undefined" && window.localStorage ? window.localStorage : new MemStorage());
    }
    /** Retrieves a value from storage */
    get(key) {
        const item = this.storage.getItem(String(key));
        if (!item)
            return Promise.resolve(null);
        try {
            const { value } = JSON.parse(item);
            return Promise.resolve(value);
        }
        catch {
            return Promise.resolve(null);
        }
    }
    /** Stores a value in storage */
    set = (key, value) => Promise.resolve(this.storage.setItem(String(key), JSON.stringify({ value })));
    /** Removes a value from storage */
    del = (key) => Promise.resolve(this.storage.removeItem(String(key)));
    /** Removes all values from storage */
    clear = () => Promise.resolve(this.storage.clear());
}
/**
 * In-memory storage implementation that implements the Storage interface
 * Useful for testing or when localStorage is not available
 * @example
 * ```ts
 * const memStorage = new MemStorage();
 * const db = new StorageKv(memStorage);
 * ```
 */
export class MemStorage {
    store;
    /** Creates a new MemStorage instance */
    constructor(store = new Map()) {
        this.store = store;
    }
    /** Retrieves an item from memory storage */
    getItem = (key) => this.store.get(key) ?? null;
    /** Stores an item in memory storage */
    setItem(key, value) {
        this.store.set(key, value);
    }
    /** Removes an item from memory storage */
    removeItem(key) {
        this.store.delete(key);
    }
    /** Removes all items from memory storage */
    clear() {
        this.store.clear();
    }
    /** Returns the number of items in storage */
    get length() {
        return this.store.size;
    }
    /** Returns the key at the given index */
    key = (index) => Array.from(this.store.keys())[index] ?? null;
}
//# sourceMappingURL=storage.js.map